import { MessageSquare } from 'lucide-react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { useSession } from '../../auth/useSession';
import { CommentThread } from '../../community/ui/CommentThread';
import { confirm } from '../../community/ui/Confirm';
import type { StudyPageDock } from '../study/StudyPage';
import { threadSize, toThread, useCommentMutations, useThread } from './api';

/**
 * The study's Discussion, docked under the right panel's tabs in both
 * editions (decided 2026-10-04): the comments scroll in their own box, the
 * composer stays under them. Through a share link it reads and writes in the
 * link's namespace. A guest (community, Public studies) is offered the way
 * to sign in; enterprise has no guests and passes none.
 */
export interface DiscussionOptions {
  shareId?: string;
  /** Where a guest goes to sign in, back to this page afterwards. */
  signin?: (nav: NavigateFunction) => void;
}

export function DiscussionThread({ studyId, shareId, signin }: DiscussionOptions & { studyId: string }) {
  const nav = useNavigate();
  const { data: me, isLoading } = useSession();
  const guest = isLoading ? undefined : !me?.userId;
  const thread = useThread('study', studyId, guest, shareId);
  const cm = useCommentMutations('study', studyId, shareId);
  return (
    <CommentThread
      composer="bottom"
      comments={toThread(thread.data?.comments ?? [])}
      me={{ name: me?.displayName || me?.email || '' }}
      canPost={!!me?.userId}
      onSignin={signin ? () => signin(nav) : undefined}
      placeholder="Comment on this study"
      emptyText="No comments yet. Start the discussion."
      onPost={(body) => cm.post.mutate({ body })}
      onReply={(parentId, body) => cm.post.mutate({ body, parentId })}
      onEdit={(commentId, body) => cm.edit.mutate({ commentId, body })}
      onDelete={async (commentId) => {
        if (await confirm({ title: 'Delete this comment?', action: 'Delete', danger: true })) cm.remove.mutate(commentId);
      }}
    />
  );
}

/** How many comments the study has, for the dock's header and the rail; nothing while unknown or none. */
export function DiscussionCount({ studyId, shareId }: { studyId: string; shareId?: string }) {
  const { data: me, isLoading } = useSession();
  const guest = isLoading ? undefined : !me?.userId;
  const thread = useThread('study', studyId, guest, shareId);
  const n = threadSize(thread.data?.comments);
  return n ? <>{n}</> : null;
}

/** The dock as a shell hands it to the study page. Build it once per share (the Count is a component). */
export function discussionDock(opts: DiscussionOptions = {}): StudyPageDock {
  return {
    id: 'discussion',
    label: 'Discussion',
    icon: <MessageSquare className="h-4 w-4" aria-hidden />,
    render: (ctx) => (ctx.studyId ? <DiscussionThread key={ctx.studyId} studyId={ctx.studyId} {...opts} /> : null),
    Count: ({ ctx }) => (ctx.studyId ? <DiscussionCount studyId={ctx.studyId} shareId={opts.shareId} /> : null),
  };
}
